import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import popup from "./popup";
import { selectAdminToken } from "../../admin/sessionSlice";
import {
  bootstrapStageAndReport,
  isGameOver,
} from "../../functions/stageReportSync";
import { setBackButtonUrl } from "../uiSlice";
import { PEOPLE, UI } from "./desiAssets";
import { GAMES, THANKYOU_ROUTE, TOTAL_GAMES, gameRoute } from "./gameConfig";
import {
  getBackendBase,
  getStoredUser,
  isDemoUser,
  loadPlayerStage,
  playedLevels,
  sumGameScores,
} from "./gameUtils";
import useDesiBackground from "./useDesiBackground";
import "./desi.css";

/** welcome.php — pick any of the six states, in any order. */
function Welcome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = useMemo(() => isDemoUser(storedUser), [storedUser]);

  const [stage, setStage] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useDesiBackground();

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [dispatch]);

  useEffect(() => {
    if (!storedUser?.userId && !storedUser?.id) {
      navigate("/", { replace: true });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      // welcome.php: first visit creates the stage rows and the external report (sendReport add).
      await bootstrapStageAndReport({
        backendBase: getBackendBase(),
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;
      const result = await loadPlayerStage({
        backendBase: getBackendBase(),
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;
      const record = result.stage;
      // Every state played: on to the thank-you page.
      if (
        record &&
        (isGameOver(record) || playedLevels(record).length >= TOTAL_GAMES)
      ) {
        navigate(THANKYOU_ROUTE, { replace: true });
        return;
      }
      setStage(record);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!loaded) {
    return (
      <div className="desi-loader">
        <div className="quiz-loader" />
      </div>
    );
  }

  const played = new Set(playedLevels(stage));

  const open = (game) => {
    if (played.has(game.level)) {
      popup.fire({
        icon: "info",
        title: "Already explored",
        text: "You have already played this state. Pick another to continue your adventure!",
        confirmButtonText: "Got it",
      });
      return;
    }
    navigate(gameRoute(game.key));
  };

  return (
    <div className="desi-page welcome-page">
      <div className="welcome-score">
        <div className="desi-pill desi-pill--score">
          Score: {sumGameScores(stage)}
        </div>
      </div>

      <img className="welcome-topline" src={UI.topLine} alt="" draggable={false} />
      <p className="welcome-caption">
        You can choose to play in any order. Earn maximum points by completing
        all stages.
      </p>

      <div className="welcome-pegs">
        {GAMES.map((game) => (
          <button
            key={game.key}
            type="button"
            className={`welcome-peg${played.has(game.level) ? " is-played" : ""}`}
            aria-label={game.state}
            onClick={() => open(game)}
          >
            <img src={PEOPLE[game.key]} alt={game.state} draggable={false} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default Welcome;
